import React, { isValidElement, cloneElement } from "react";
import Popover from "@mui/material/Popover";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";

/**
 * Type for a single menu item in the header.
 */
export type HeaderMenuItem = {
  label: string;
  href?: string;
  action?: () => void;
  icon?: React.ReactNode;
  showWhenLoggedIn?: boolean;
  showWhenLoggedOut?: boolean;
  external?: boolean;
  highlight?: boolean;
  children?: HeaderMenuItem[];
};

/**
 * Props for MorePopup component.
 */
interface MorePopupProps {
  id: string | undefined;
  open: boolean;
  anchorEl: null | HTMLElement;
  handleClose: () => void;
  items: HeaderMenuItem[];
}

/**
 * MorePopup renders a dynamic list of menu items in a Popover.
 * @param {MorePopupProps} props
 */
const MorePopup: React.FC<MorePopupProps> = ({
  id,
  open,
  anchorEl,
  handleClose,
  items,
}) => {
  const { t } = useTranslation();

  /**
   * Render a single menu item (link or button), ensuring icon sizing.
   */
  const renderMenuItem = (item: HeaderMenuItem, key: string | number) => {
    let iconNode: React.ReactNode = null;
    if (item.icon && isValidElement(item.icon)) {

      if (item.icon.type === "img") {
        iconNode = cloneElement(item.icon, {
          ...item.icon.props,
          className: item.icon.props && item.icon.props.className
            ? item.icon.props.className
            : "max-w-[24px] max-h-[24px]",
        });
      } else {
        iconNode = item.icon;
      }
    }
    if (item.href) {
      if (item.external) {
        // External link
        return (
          <a
            key={key}
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleClose}
            className="flex gap-3 items-center custom-text-[poppins-400-14px] leading-[24px] p-2 no-underline transition-colors hover:text-blue"
          >
            {iconNode}
            <span>{t(item.label)}</span>
          </a>
        );
      }
      // Internal navigation
      return (
        <NavLink
          to={item.href}
          key={key}
          onClick={handleClose}
          className="flex gap-3 items-center custom-text-[poppins-400-14px] leading-[24px] p-2 no-underline transition-colors hover:text-blue"
        >
          {iconNode}
          <span>{t(item.label)}</span>
        </NavLink>
      );
    }
    if (item.action) {
      return (
        <button
          key={key}
          type="button"
          onClick={() => {
            item.action && item.action();
            handleClose();
          }}
          className="flex gap-3 items-center custom-text-[poppins-400-14px] leading-[24px] p-2 no-underline transition-colors hover:text-blue bg-transparent border-none w-full text-left"
        >
          {iconNode}
          <span>{t(item.label)}</span>
        </button>
      );
    }
    return null;
  };

  return (
    <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: 30,
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      slotProps={{
        paper: {
          className: "!rounded-[20px] !shadow-[0px_7px_29px_0px_rgba(100,100,111,0.20)]",
        },
      }}
    >
      <div className="px-[16px] py-[26px] min-w-[180px]">
        {items && items.length > 0 ? (
          items.map((item, idx) => renderMenuItem(item, idx))
        ) : (
          <span className="custom-text-[poppins-400-14px] text-gray-400">No options</span>
        )}
      </div>
    </Popover>
  );
};

export default MorePopup;